import { isValidYMD } from './date-utils'

export const LEAVE_TYPES = ['SICK', 'CASUAL', 'ANNUAL', 'UNPAID', 'OTHER'] as const
export type LeaveType = typeof LEAVE_TYPES[number]

export const REQUEST_STATUS = ['PENDING', 'APPROVED', 'REJECTED'] as const
export type RequestStatus = typeof REQUEST_STATUS[number]

export const TASK_STATUS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const
export type TaskStatus = typeof TASK_STATUS[number]

export const TASK_PRIORITY = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const
export type TaskPriority = typeof TASK_PRIORITY[number]

/** Calendar entries declared by Admin/HR — see src/server/routes/events.ts. */
export const EVENT_TYPES = ['EVENT', 'MEETING', 'TRAINING', 'DEADLINE', 'ANNOUNCEMENT'] as const
export type EventType = typeof EVENT_TYPES[number]

/** ALL = whole company; SELECTED = only the listed attendees. */
export const EVENT_AUDIENCES = ['ALL', 'SELECTED'] as const
export type EventAudience = typeof EVENT_AUDIENCES[number]

export const USER_ROLES = ['ADMIN', 'EMPLOYEE', 'HR', 'INTERN'] as const
export type UserRole = typeof USER_ROLES[number]

/**
 * Roles that get a monthly stipend report generated for them. Interns are paid
 * the same way employees are, and HR/Admin staff are included so their pay can
 * be calculated from the same workflow.
 */
export const STIPEND_ROLES = ['EMPLOYEE', 'INTERN', 'HR', 'ADMIN'] as const

/** Roles that use the employee-facing dashboard rather than the admin shell. */
export const STAFF_ROLES = ['EMPLOYEE', 'INTERN'] as const

export interface DateRangeError {
  ok: false
  error: string
}
export interface DateRangeOk {
  ok: true
  startDate: string
  endDate: string
}

// Validate startDate/endDate pair: both YYYY-MM-DD, start <= end, optional
// no-past-dates rule, optional max range.
export function validateDateRange(
  startDate: unknown,
  endDate: unknown,
  opts?: { allowPast?: boolean; maxDays?: number; todayYMD?: string }
): DateRangeError | DateRangeOk {
  if (!isValidYMD(startDate)) return { ok: false, error: 'startDate must be YYYY-MM-DD' }
  if (!isValidYMD(endDate)) return { ok: false, error: 'endDate must be YYYY-MM-DD' }
  if (endDate < startDate) return { ok: false, error: 'endDate must be on or after startDate' }
  if (opts && opts.allowPast === false && opts.todayYMD && startDate < opts.todayYMD) {
    return { ok: false, error: 'startDate cannot be in the past' }
  }
  if (opts?.maxDays) {
    const s = new Date(startDate + 'T00:00:00Z').getTime()
    const e = new Date(endDate + 'T00:00:00Z').getTime()
    const days = Math.round((e - s) / 86_400_000) + 1
    if (days > opts.maxDays) return { ok: false, error: `range exceeds ${opts.maxDays} days` }
  }
  return { ok: true, startDate, endDate }
}

export function validateNumberInRange(
  value: unknown,
  field: string,
  min: number,
  max: number
): { ok: true; value: number } | { ok: false; error: string } {
  const n = Number(value)
  if (!Number.isFinite(n)) return { ok: false, error: `${field} must be a number` }
  if (n < min || n > max) return { ok: false, error: `${field} must be between ${min} and ${max}` }
  return { ok: true, value: n }
}

export function isOneOf<T extends readonly string[]>(value: unknown, allowed: T): value is T[number] {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
}

export function wordCount(text: string): number {
  return (text.trim().match(/\S+/g) || []).length
}
